/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  NgModule,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  FormGroupDirective,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { DisplayConditionPipeModule } from '@domains/companies/register-form/form-question-group/display-condition.pipe';
import { CheckboxModule } from '@domains/companies/register-form/form-question/checkbox/checkbox.component';
import { DropdownModule } from '@domains/companies/register-form/form-question/dropdown/dropdown.component';
import { FileModule } from '@domains/companies/register-form/form-question/file/file.component';
import { LocationPickerModule } from '@domains/companies/register-form/form-question/location-picker/location-picker.component';
import { NumberModule } from '@domains/companies/register-form/form-question/number/number.component';
import { TextModule } from '@domains/companies/register-form/form-question/text/text.component';
import { TextareaModule } from '@domains/companies/register-form/form-question/textarea/textarea.component';
import { YesNoModule } from '@domains/companies/register-form/form-question/yes-no/yes-no.component';
import {
  FormQuestion,
  FormQuestionGroup,
  FormQuestionSubGroup,
  FormQuestionType,
} from '@models/company/company-register';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ScrollToService } from '@nicky-lenaers/ngx-scroll-to';
import { WINDOW } from '@wizbii/angular-utilities';
import { fadeInOnEnterAnimation, slideInDownOnEnterAnimation } from 'angular-animations';
import { BehaviorSubject, Subscription } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'app-form-question-group',
  templateUrl: './form-question-group.component.html',
  styleUrls: ['./form-question-group.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormQuestionGroupComponent),
      multi: true,
    },
    { provide: NG_VALIDATORS, multi: true, useExisting: forwardRef(() => FormQuestionGroupComponent) },
  ],
  animations: [
    fadeInOnEnterAnimation({ duration: 300 }),
    slideInDownOnEnterAnimation({ duration: 300, translate: '4.375rem' }),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormQuestionGroupComponent implements OnInit, OnDestroy, AfterViewInit, ControlValueAccessor, Validator {
  @Input() questionGroup!: FormQuestionGroup;
  @Input() index!: number;
  @Input() model!: Record<string, string>;
  @Input() enableScroll = true;
  @Input() isFlat = false;

  @Output() nextQuestion = new EventEmitter<number>();
  @Output() skipQuestion = new EventEmitter<number>();

  FormQuestionType = FormQuestionType;

  private readonly focusableFormQuestionTypes = [
    FormQuestionType.Text,
    FormQuestionType.Siret,
    FormQuestionType.Phone,
    FormQuestionType.City,
    FormQuestionType.Email,
    FormQuestionType.Number,
    FormQuestionType.Textarea,
  ];

  formGroup!: FormGroup;

  formSubmitSubscription!: Subscription;

  _value: any;
  _disabled!: boolean;

  set value(val: any) {
    if (this._value === val) {
      return;
    }

    this._value = val;
    this.onChange(val);
    this.onTouched();
  }

  get value(): any {
    return this._value;
  }

  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(isDisabled: boolean) {
    this._disabled = isDisabled;
    if (isDisabled) {
      this.formGroup.disable();
    } else {
      this.formGroup.enable();
    }
  }

  get questionGroupInvalid(): boolean {
    return !!this.questionGroup?.validateCondition && !this.questionGroup.validateCondition(this.formGroup);
  }

  get title(): string {
    return this.questionGroup?.title ?? '';
  }

  get subtitle(): string {
    return this.questionGroup?.subtitle ?? '';
  }

  get description(): string {
    return this.questionGroup?.description ?? '';
  }

  questionGroupError$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private readonly scrollToService: ScrollToService,
    private readonly element: ElementRef,
    private readonly controlContainer: ControlContainer,
    @Inject(WINDOW) private readonly window: any
  ) {}

  ngOnInit(): void {
    this.formGroup = this.initFormGroup(this.questionGroup.subGroups);

    this.formGroup.valueChanges.pipe(untilDestroyed(this)).subscribe({
      next: (val: any) => {
        this.value = val;
        this.questionGroupError$.next(this.questionGroupInvalid);
      },
    });

    if (this.controlContainer?.formDirective) {
      this.formSubmitSubscription = (this.controlContainer.formDirective as FormGroupDirective).ngSubmit
        .pipe(untilDestroyed(this))
        .subscribe({
          next: () => {
            this.formGroup.markAllAsTouched();
            this.formGroup.updateValueAndValidity();
          },
        });
    }

    this.cdr.detectChanges();
  }

  ngAfterViewInit(): void {
    this.window.setTimeout(() => {
      this.value = this.formGroup.value;
      this.cdr.detectChanges();

      if (this.index > 0 && this.enableScroll) {
        this.scrollToService.scrollTo({ target: `#questionGroup${this.index}`, offset: -50 });
        this.setFocusFirstField();
      }
    });
  }

  initFormGroup(questionGroup: FormQuestionSubGroup[]): FormGroup {
    let group = {};

    questionGroup.forEach((subGroup) => {
      subGroup.questions.forEach((question) => {
        const controlName = question.formControlName;
        group = {
          ...group,
          [controlName]: this.initFormControlValue(controlName, question.type),
        };

        this.cdr.detectChanges();
      });
    });
    return this.formBuilder.group(group);
  }

  initFormControlValue(controlName: string, questionType: FormQuestionType): FormControl {
    if (this.model) {
      return new FormControl(
        questionType === FormQuestionType.Checkbox ? !!this.model[controlName] : this.model[controlName]
      );
    }
    return new FormControl(questionType === FormQuestionType.Checkbox ? false : undefined);
  }

  onChange: (val: any) => void = () => {
    // intentional empty arrow function
  };
  onTouched: () => void = () => {
    // intentional empty arrow function
  };

  writeValue(val: any): void {
    this.formGroup.setValue({ ...this.formGroup.value, ...val });
    this.value = val;
    this.questionGroupError$.next(false);
  }

  registerOnChange(fn: (val: any) => void): void {
    this.onChange = (value) => {
      fn(value);
      this.cdr.markForCheck();
    };
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = () => {
      fn();
      this.cdr.markForCheck();
    };
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  computeNext(): void {
    if (this.formGroup.invalid || this.questionGroupInvalid) {
      this.questionGroupError$.next(true);
      return;
    }
    this.questionGroupError$.next(false);
    this.nextQuestion.emit(this.index);
  }

  validate(_: any): ValidationErrors | null {
    const error = { error: true };

    return this.formGroup.invalid || this.questionGroupInvalid ? error : null;
  }

  private setFocusFirstField(): void {
    const firstQuestion = this.questionGroup.subGroups[0].questions[0];
    if (this.focusableFormQuestionTypes.includes(firstQuestion.type)) {
      const elmToFocus = this.element.nativeElement.querySelector(`#${firstQuestion.formControlName}-${this.index}`);
      if (elmToFocus) {
        elmToFocus.focus();
        this.cdr.detectChanges();
      }
    }
  }

  trackBySubGroupIndex(index: number, _: FormQuestionSubGroup): number {
    return index;
  }

  trackByQuestionType(_: number, question: FormQuestion): FormQuestionType {
    return question.type;
  }

  ngOnDestroy(): void {
    this.questionGroupError$.complete();
  }
}

@NgModule({
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TextModule,
    LocationPickerModule,
    NumberModule,
    TextareaModule,
    YesNoModule,
    CheckboxModule,
    FileModule,
    DropdownModule,
    DisplayConditionPipeModule,
  ],
  declarations: [FormQuestionGroupComponent],
  exports: [FormQuestionGroupComponent],
})
export class FormQuestionGroupModule {}
